import { assertUncomputedFromBase, FromBaseInputMissing } from '../janus-engine/compile/from-base-context';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { inventoryCatalogScope, mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { createNpcDefinitionFinalizer } from '../janus-engine/compile/from-base-npc-finalization';
import { createAncestryCardFinalizer } from './ancestry-card-finalizer';
import type { AncestryCardMetadata } from './ancestry-card-finalizer';
import { creatorNode } from '../janus-engine/character-creator/panels';
import { floatval } from '../janus-engine/php-compat';
import { layerEntriesOf, layerOf } from '../shared/array-types';
import type { CatalogCard, CatalogResponse } from './types';

export const ANCESTRY_DEFINITION_CARDS_SCHEMA = 'raw-ancestry-definition-cards-v1';
export const ANCESTRY_CARD_SURFACE_SCHEMA = 'raw-ancestry-definition-cards-v2';
export interface AncestryCardSurfaceResponse {
    schema: typeof ANCESTRY_CARD_SURFACE_SCHEMA;
    family: 'ancestries';
    definition_ids: string[];
    base: FromBaseRow;
    card_metadata: AncestryCardMetadata;
    total: number;
}
export interface AncestryDefinitionCardsResponse {
    schema: typeof ANCESTRY_DEFINITION_CARDS_SCHEMA;
    family: 'ancestries';
    definition_ids: string[];
    base: FromBaseRow;
    shared: InventorySharedCatalog;
    total: number;
}

/** Both envelopes use the same card readers. V2 completes only their saved
 * passive/provider/media window; V1 remains readable during bundle delivery. */
export function ancestryDefinitionCards(input: AncestryDefinitionCardsResponse | AncestryCardSurfaceResponse,
    projectCard: (row: FromBaseRow, base: FromBaseRow, cdnUrl?: string) => CatalogCard): CatalogResponse {
    if (input.family !== 'ancestries' || !Array.isArray(input.definition_ids)
        || input.total !== input.definition_ids.length) throw new Error('catalog-ancestry-definition-schema');
    const sections: CatalogResponse['sections'] = { standard: [], advanced: [] };
    if (!input.total) return { sections, total: 0 };
    assertUncomputedFromBase(input.base);
    const { base, finalize, cdnUrl } = ancestryCardContext(input);
    for (const id of input.definition_ids) {
        const ancestry = finalize.definition(id, []);
        if (!ancestry) throw new FromBaseInputMissing('root-definition', id);
        const passive = layerEntriesOf(ancestry, 'passive_ability').map(entry => creatorNode(entry, ancestry))
            .find(row => Number(layerOf(row, 'requires_level')) === 1);
        const card = projectCard(ancestry, base, cdnUrl);
        // Ancestry cards have no leading or corner icons, unlike subclass cards.
        delete card.icon_id;
        delete card.badge_id;
        delete card.requires_class;
        card.essences = Object.fromEntries(['ancestral_body', 'ancestral_mind', 'ancestral_soul', 'ancestral_fortune'].map(key => {
            const value = layerOf(passive, key);
            return [key, value == null ? null : floatval(String(value).replace(/\+/g, '')).value];
        }));
        const standard = !card.source || card.source.source_id?.toUpperCase() === 'D887B8A7-2CED-AB81-3E4E-DDDF4ACD91AE';
        sections[standard ? 'standard' : 'advanced'].push(card);
    }
    return { sections, total: input.total };
}

function ancestryCardContext(input: AncestryDefinitionCardsResponse | AncestryCardSurfaceResponse) {
    if (input.schema === ANCESTRY_CARD_SURFACE_SCHEMA) {
        return { base: input.base, finalize: createAncestryCardFinalizer(input.base, input.card_metadata), cdnUrl: input.card_metadata.cdnUrl };
    }
    assertUncomputedFromBase(input.shared);
    const base = inventoryCatalogScope(input.shared, input.base).base;
    const metadata = entityRuntimeMetadata(input.shared.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
    const inventory = mergeInventoryMetadata(metadata.inventory, base.inventory_metadata);
    return { base, finalize: createNpcDefinitionFinalizer(base, metadata.player, inventory), cdnUrl: inventory.cdnUrl };
}
